import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/feature/Seo";
import { brand } from "@/mocks/siteData";
import StoriesHero from "./components/StoriesHero";
import FeaturedStory from "./components/FeaturedStory";
import StoryGallery from "./components/StoryGallery";
import StoryGrid from "./components/StoryGrid";
import StoriesCta from "./components/StoriesCta";
import { usePublicStories } from "@/hooks/usePublicStories";

export default function Stories() {
  const { stories, featured, loading, error, reload } = usePublicStories();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const gridStories = featured ? stories.filter((story) => story.id !== featured.id) : stories;

  const authorCount = new Set(stories.map((story) => story.author)).size;

  const galleryImages =
    featured && featured.gallery.length > 0
      ? featured.gallery
      : gridStories.map((story) => ({ src: story.cover, alt: story.title }));

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      <Seo
        title={`Stories From the Soil | ${brand.name}`}
        description="Read farmer stories, seasonal notes and field diaries from the farms of Konaseema — real voices from the people who grow your food."
        path="/stories"
        keywords="farmer stories India, agri tourism blog, Konaseema farm stories, rural India stories, field diaries"
        image="https://readdy.ai/api/search-image?query=An%20open%20journal%20and%20a%20farmer%20walking%20through%20lush%20green%20paddy%20fields%20in%20rural%20India%20at%20sunrise%2C%20warm%20golden%20light%2C%20fresh%20green%20tones%2C%20cinematic%20style%2C%20ultra%20detailed&width=1920&height=1080&seq=stories-list-cover&orientation=landscape"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Stories From the Soil",
          description: "Farmer stories and field diaries from the farms of Konaseema, India.",
          publisher: { "@type": "Organization", name: brand.fullName },
          inLanguage: "en",
        }}
      />
      <Navbar />
      <main>
        <StoriesHero total={stories.length} authorCount={authorCount} />

        {loading ? (
          <section className="bg-background-50 py-24 flex flex-col items-center gap-3">
            <i className="ri-loader-4-line text-3xl animate-spin text-primary-500"></i>
            <p className="text-sm text-foreground-600">Loading the journal…</p>
          </section>
        ) : error ? (
          <section className="bg-background-50 py-24">
            <div className="w-full px-4 md:px-8">
              <div className="max-w-xl mx-auto bg-accent-100 border border-accent-300 rounded-lg px-5 py-6 text-center">
                <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-200 text-accent-900">
                  <i className="ri-error-warning-line text-2xl"></i>
                </span>
                <p className="text-sm text-accent-900 mt-4">{error}</p>
                <button
                  type="button"
                  onClick={reload}
                  className="mt-5 inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-5 py-3 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line"></i> Try again
                </button>
              </div>
            </div>
          </section>
        ) : stories.length === 0 ? (
          <section className="bg-background-50 py-24">
            <div className="w-full px-4 md:px-8 text-center">
              <i className="ri-book-open-line text-4xl text-foreground-400"></i>
              <h2 className="font-heading text-2xl text-foreground-950 font-semibold mt-4">
                The journal is being written
              </h2>
              <p className="text-foreground-600 mt-2">New stories will appear here soon.</p>
            </div>
          </section>
        ) : (
          <>
            {featured ? <FeaturedStory story={featured} /> : null}
            <StoryGallery images={galleryImages} />
            <StoryGrid stories={gridStories} />
          </>
        )}

        <StoriesCta />
      </main>
      <Footer />
    </div>
  );
}